import React, { useState } from 'react';
import { Check, ArrowRight, Building2, Truck, Sparkles, ShieldCheck } from 'lucide-react';
import PageHeader from '@/components/ui/PageHeader';

export default function B2B() {
  const [submitted, setSubmitted] = useState(false);

  const onSubmit = (e) => {
    e.preventDefault();
    setSubmitted(true);
    setTimeout(() => setSubmitted(false), 4000);
  };

  return (
    <div data-testid="page-b2b">
      <PageHeader
        eyebrow="B2B & Wholesale"
        title="For cafés, hotels"
        accent="and the people who curate taste."
        description="Bring Sip Theory to your menu. Refrigerated delivery, white-glove onboarding and pricing built for repeat orders."
      />

      <section className="container-x py-14 grid grid-cols-1 lg:grid-cols-12 gap-10">
        {/* benefits */}
        <div className="lg:col-span-7 space-y-6">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
            <Benefit icon={<Truck className="w-5 h-5" />} title="Cold Chain Delivery" body="Refrigerated trucks straight to your door, scheduled around your service hours." />
            <Benefit icon={<Building2 className="w-5 h-5" />} title="Custom Volume Pricing" body="Per-pallet rates that improve as your weekly order grows." />
            <Benefit icon={<Sparkles className="w-5 h-5" />} title="Bespoke Recipes" body="Exclusive flavour collaborations for menus, retreats and signature events." />
            <Benefit icon={<ShieldCheck className="w-5 h-5" />} title="Quality Assurance" body="Batch-tested, traceability QR on every bottle, FSSAI certified." />
          </div>

          <div className="glass-card rounded-3xl p-8 lg:p-10">
            <h3 className="font-display text-2xl text-bone-50">Who we partner with</h3>
            <ul className="mt-5 grid grid-cols-1 md:grid-cols-2 gap-3">
              {['Specialty cafés & roasters', 'Boutique hotels & resorts', 'Fine-dining & cloud kitchens', 'Premium retail & gourmet stores', 'Corporate pantries', 'Wellness studios & gyms'].map((p) => (
                <li key={p} className="flex items-center gap-3 text-bone-100/90 text-sm">
                  <Check className="w-4 h-4 text-sage-300" /> {p}
                </li>
              ))}
            </ul>
          </div>
        </div>

        {/* form */}
        <aside className="lg:col-span-5">
          <form
            onSubmit={onSubmit}
            data-testid="b2b-form"
            className="glass-card rounded-3xl p-8 lg:p-10 space-y-4 sticky top-28"
          >
            <span className="eyebrow">Request a quote</span>
            <h3 className="font-display text-3xl text-bone-50">Let&apos;s start a conversation</h3>

            <Field label="Business name" name="business" required testid="b2b-business" />
            <Field label="Your name" name="name" required testid="b2b-name" />
            <Field label="Email" name="email" type="email" required testid="b2b-email" />
            <Field label="Phone" name="phone" type="tel" testid="b2b-phone" />
            <Field label="Estimated monthly volume (bottles)" name="volume" testid="b2b-volume" />

            <div>
              <label className="text-[11px] tracking-[0.22em] uppercase text-bone-300/80">Message</label>
              <textarea
                rows={4}
                className="mt-2 w-full bg-ink-800 border border-bone-100/12 rounded-2xl px-4 py-3 text-sm text-bone-100 placeholder:text-bone-300/50 outline-none focus:border-gold/60"
                placeholder="Tell us about your business and what you'd like to serve."
                data-testid="b2b-message"
              />
            </div>

            <button type="submit" className="btn-primary w-full justify-center" data-testid="b2b-submit">
              Submit Request <ArrowRight className="w-4 h-4" />
            </button>
            {submitted && (
              <p className="text-sage-300 text-sm" data-testid="b2b-success">Thank you — our partnerships team will be in touch within 24 hours.</p>
            )}
          </form>
        </aside>
      </section>
    </div>
  );
}

const Benefit = ({ icon, title, body }) => (
  <div className="glass-card rounded-3xl p-6">
    <span className="w-11 h-11 rounded-full bg-gold/10 border border-gold/30 flex items-center justify-center text-gold">{icon}</span>
    <h4 className="font-display text-xl text-bone-50 mt-4">{title}</h4>
    <p className="text-sm text-bone-200/85 mt-2 leading-relaxed">{body}</p>
  </div>
);

const Field = ({ label, name, type = 'text', required, testid }) => (
  <div>
    <label className="text-[11px] tracking-[0.22em] uppercase text-bone-300/80">{label}{required && <span className="text-gold ml-1">*</span>}</label>
    <input
      name={name}
      type={type}
      required={required}
      data-testid={testid}
      className="mt-2 w-full bg-ink-800 border border-bone-100/12 rounded-full px-5 py-3 text-sm text-bone-100 placeholder:text-bone-300/50 outline-none focus:border-gold/60"
    />
  </div>
);
